import React, { useCallback } from 'react'
import { Button, Form } from 'react-bootstrap'
import { Row, Col } from 'react-bootstrap'
import { ExclamationCircleFill, Calendar3Week } from 'react-bootstrap-icons'
import useCourseMutation from '../../../hooks/use-course-mutuation'
import useToasts from '../../../hooks/use-toasts'
import useViewport from '../../../hooks/use-viewport'
import Facilitator from './facilitator'
import Location from './location'
import Credits from './credits'
import Enrollees from './enrollees'
import Droppers from './droppers'
import useCourseValidation from '../../../hooks/use-course-validation'
import Title from './title'
import Description from './description'
import DimensionSelector from './dimensions'
import Occurrences from './occurrences'
import Occurrence from './occurrence'
import CapacityMinimum from './capacity-minimum'
import CapacityMaximum from './capacity-maximum'
import RequirementsEditor from '../requirements-editor'
import GradeLevelsEditor from '../grade-levels-editor'
import { get } from 'lodash'
import SeriesToggle from './series-toggle'
import SeriesLabel from './series-label'
import PrivateToggle from './private-toggle'
import Spinner from './spinner'
import hooks from '../../../hooks'
import EnrollCutoff from './enroll-cutoff'
import DropCutoff from './drop-cutoff'
import SecretToggle from './secret-toggle'
import RolloutDate from './rollout-date'

const OccurrencesLabel = ({
    children,
    label = 'Occurrences'
}) => {
    const { breakpoint, breakpoints } = useViewport()
    return (
        <Form.Group as={Row}>
            <Form.Label column  sm={12} md={1} lg={1} xl={3} style={{ paddingRight: '0px' }}>
                <Calendar3Week size={30} />  {breakpoint >= breakpoints.XL || breakpoint <= breakpoints.SM ? label : ''}
            </Form.Label>
            <Col sm={12} md={11} lg={11} xl={9}>
                {children}
            </Col>
        </Form.Group>
    )
}

const EditCourseForm = ({
    experienceId,
    onClose = () => {},
    saveAsNew,
}) => {
    const { success: successToast, toastError } = useToasts({
        onSuccess: onClose
    })

    const onValidationError = useCallback((err) => {
        toastError(err)
    }, [toastError])

    const {
        course,
        setCourse,
        isLoading,
        isSaving,
        isBusy,
        save
    } = useCourseMutation({
        courseId: experienceId,
        onSuccess: () => successToast('Experience saved!'),
        onError: toastError,
        saveAsNew
    })

    const isNewCourse = !course.id || saveAsNew

    const { showConfirmation } = hooks.useConfirmationDialog({
        onConfirm: save,
        title: 'No Grades Selected',
        description: 'You have not selected any grades.  Are you sure you want to create this experience?  It will not show up to students.'
    })

    const finalChecks = React.useCallback(() => {
        if (course.gradeLevels && !course.gradeLevels.length) {
            showConfirmation()
        } else {
            save()
        }
    }, [course, save, showConfirmation])

    const {
        validateCourse,
    } = useCourseValidation({ onSuccess: finalChecks, onError: onValidationError, isNew: isNewCourse})

    if (isLoading) {
        return ( <Spinner label='Loading...' /> )
    }
    if (isSaving) {
        return ( <Spinner label={isNewCourse ? 'Saving...' : 'Updating...'} /> )
    }

    const renderOccurrences = () => {
        if(!isNewCourse) {
            return null
        }
        return (
            <OccurrencesLabel>
                <Occurrences
                    value={course.occurrences}
                    onChange={occurrences => setCourse({ ...course, occurrences })}
                />
            </OccurrencesLabel>
        )
    }

    const renderOccurrence = () => {
        if(isNewCourse) {
            return null
        }
        return (
            <OccurrencesLabel label='Occurrence'>
                <Occurrence
                    value={{ date: course.date, hourId: course.hourId, durationId: course.durationId }}
                    onChange={({ date, hourId, durationId }) => setCourse({ ...course, date, hourId, durationId })}
                    disabled={get(course, 'enrollees', []).length}
                />
            </OccurrencesLabel>
        )
    }

    return (
        <div>
            <Row><Col className='text-center'><h3>{!isNewCourse ? 'Edit' : 'Create'} Experience</h3></Col></Row>
            <Row>
                <Col>
                    <Title value={course.name} onChange={name => setCourse({ ...course, name })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Description value={course.description} onChange={description => setCourse({ ...course, description })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <DimensionSelector value={course.dimensions} onChange={dimensions => setCourse({ ...course, dimensions })} />
                </Col>
                <Col>
                    <Facilitator value={course.facilitatorId} onChange={facilitatorId => setCourse({ ...course, facilitatorId })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Location value={course.locationId} onChange={locationId => setCourse({ ...course, locationId })} />
                </Col>
                <Col>
                    <Credits value={course.credits} onChange={credits => setCourse({ ...course, credits })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <CapacityMinimum value={course.minCapacity} onChange={minCapacity => setCourse({ ...course, minCapacity })} />
                </Col>
                <Col>
                    <CapacityMaximum value={course.maxCapacity} onChange={maxCapacity => setCourse({ ...course, maxCapacity })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <EnrollCutoff disabled={Boolean(course.isSeries) && Boolean(experienceId)} value={course.enrollCutoff} onChange={enrollCutoff => setCourse({ ...course, enrollCutoff })} />
                </Col>
                <Col>
                    <DropCutoff disabled={Boolean(course.isSeries) && Boolean(experienceId)} value={course.dropCutoff} onChange={dropCutoff => setCourse({ ...course, dropCutoff })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <SecretToggle value={course.isSecretEnrollment} onChange={isSecretEnrollment => setCourse({ ...course, isSecretEnrollment })} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <PrivateToggle value={course.private} onChange={p => setCourse({ ...course, ['private']: p })} />
                </Col>
            </Row>
            <RolloutDate course={course} onChange={setCourse} />
            <Row>
                <Col>
                    <SeriesToggle
                        visible={!experienceId || saveAsNew}
                        value={course.isSeries}
                        onChange={isSeries => setCourse({ ...course, isSeries })}
                    />
                    {get(course, 'series') && <SeriesLabel experience={course} />}
                </Col>
            </Row>
            <Row>
                <Col>
                    {renderOccurrences()}
                    {renderOccurrence()}
                </Col>
            </Row>
            <Row>
                <Col sm={12} md={6}>
                    <div>
                        <ExclamationCircleFill size={30} />
                        <span style={{ paddingLeft: '10px'}}>Other Requirements</span>
                    </div>
                    <div style={{ paddingLeft: '20px', paddingTop: '15px' }}>
                        <div>
                            <GradeLevelsEditor
                                value={course.gradeLevels}
                                onChange={gradeLevels => setCourse({ ...course, gradeLevels })}
                            />
                        </div>
                        <RequirementsEditor
                            requirements={course.requirements}
                            onRequirementsChange={requirements => setCourse({ ...course, requirements })}
                        />
                    </div>
                </Col>
                <Col sm={12} md={6}>
                    <Enrollees users={get(course, 'enrollees', [])} />
                    <Droppers users={get(course, 'drops', [])} />
                </Col>
                
            </Row>

            <Row>
                <Col>
                    <div style={{ display: 'flex', justifyContent: 'space-evenly'}}>
                        <Button
                            style={{ width: '160px' }}
                            variant='secondary'
                            onClick={() => onClose()}
                            disabled={isBusy}
                        >
                            Discard {!isNewCourse ? ' Changes' : '' }
                        </Button>
                        <Button
                            style={{ width: '160px' }}
                            onClick={() => validateCourse(course)}
                            disabled={isBusy}
                        >
                            {!isNewCourse ? 'Save' : 'Create'} Experience
                        </Button>
                    </div>                            
                </Col>
            </Row>
        </div>
    )
}

export default EditCourseForm